<template>
  <lp-modal
    v-if="closeWarningTitle && closeWarningContent"
    v-model="confirmModalState"
    :closable="false"
    size="sm"
    compact
    priority-position
  >
    <template #header>
      {{ closeWarningTitle }}
    </template>
    <template #body>
      <div>
        <span class="pb-2 d-block">{{ closeWarningContent }}</span>
        <div class="my-1 ly-row ly-end-center">
          <lp-button
            class="text-dark text-small font-weight-bold border-gunmetal-14"
            @click="confirmModalState = false"
          >
            {{ closeWarningStayButton }}
          </lp-button>
          <lp-button
            class="text-dark text-small font-weight-bold border-gunmetal-14"
            @click="finish"
          >
            {{ closeWarningExitButton }}
          </lp-button>
        </div>
      </div>
    </template>
  </lp-modal>
  <div v-if="isOpen && withScrim" class="guided-tour-scrim"></div>
  <teleport to="body" :disabled="disableTeleport">
    <div
      v-if="isOpen"
      :id="`step${activeIndex}`"
      :key="activeIndex"
      ref="currentStepEl"
      :data-ua-unique-id="dataUaUniqueId"
      class="guided-tour-step card p-absolute"
      :class="[
        activeIndex !== 0 && placement ? `${arrowPlacement} ${placement}` : '',
        activeIndex === 0 ? 'initial-step' : '',
        `step-${currentStep.size}`,
        currentStep.forceSize && 'force-size',
        isNewLayout && activeIndex === 0 && 'new-layout',
      ]"
      :style="
        activeIndex === 0
          ? `position: fixed; top: 50%;`
          : `top: ${currentStepPosition.top}; left: ${currentStepPosition.left}`
      "
      role="dialog"
      aria-modal="true"
    >
      <div
        class="guided-tour-step--header"
        :class="[
          !hasImageAndIsNewLayout && 'ly-row ly-spacebetween-start',
          hasImageAndIsNewLayout && 'ly-col',
        ]"
      >
        <lp-guided-tour-step-image
          v-if="hasImageAndIsNewLayout"
          :class="[hasImageAndIsNewLayout]"
          :image="currentStep.img"
          :on-load="setPopoverPosition"
        />
        <p
          class="guided-tour-step--header--title h4"
          :class="[
            isNewLayout && 'text-center h5 mb-1',
            !isNewLayout && 'h4',
            hasImageAndIsNewLayout && 'mt-2',
          ]"
        >
          {{ currentStep.title }}
        </p>
        <a
          v-if="!hasImageAndIsNewLayout"
          role="button"
          :aria-label="closeTranslation"
          @click="abort"
        >
          <i class="lpi-times text-dark-muted"></i>
        </a>
      </div>
      <p
        v-if="!currentStep.asHtml"
        class="guided-tour-step--text"
        :class="[isNewLayout && 'mb-2']"
      >
        {{ currentStep.text }}
      </p>
      <p
        v-if="currentStep.asHtml"
        :class="[isNewLayout && 'mb-2']"
        v-html="currentStep.text"
      ></p>
      <lp-guided-tour-step-image
        v-if="
          introductionStyle === GuidedTourIntroductionStepStyles.CLASSIC &&
          currentStep.img
        "
        :image="currentStep.img"
        :on-load="setPopoverPosition"
      />
      <div class="guided-tour-step--footer">
        <div
          v-if="activeIndex === 0"
          class="guided-tour-step--footer-actions"
          :class="[
            isNewLayout && 'ly-even ly-row-reverse ly-spacearound-center',
          ]"
        >
          <a
            v-if="activeIndex === 0"
            class="btn btn-primary"
            :class="[isNewLayout && 'np', !isNewLayout && 'w-100']"
            role="button"
            tabindex="0"
            :aria-label="startTranslation"
            @click="nextStep"
            @keyup.enter="nextStep"
            @keyup.space="nextStep"
          >
            {{ startTranslation }}
          </a>
          <a
            v-if="activeIndex === 0"
            class="d-block text-center"
            :class="[
              isNewLayout && 'btn btn-neutral text-dark border-gunmetal-14',
              !isNewLayout && 'pt-1 text-dark-muted text-hover-blue',
            ]"
            tabindex="0"
            role="button"
            :aria-label="skipTranslation"
            @click="finish"
            @keyup.enter="finish"
            @keyup.space="finish"
          >
            {{ skipTranslation }}
          </a>
        </div>
        <div
          v-if="activeIndex === 0 && isNewLayout"
          :class="[isNewLayout && 'mt-1 text-center text-gunmetal-52 w-100']"
        >
          {{ activeIndex }} of {{ steps.length - 1 }}
        </div>
        <div v-if="activeIndex !== 0" class="ly-row ly-spacebetween-center">
          <span class="ly-row">
            <span :class="[isNewLayout && 'text-gunmetal-52']">
              {{ activeIndex }}/{{ steps.length - 1 }}
            </span>
            <a
              v-if="activeIndex !== 0 && !isNewLayout"
              class="d-block text-center text-dark-muted text-hover-blue ml-1"
              tabindex="0"
              role="button"
              :aria-label="skipTranslation"
              @click="finish"
              @keyup.enter="finish"
              @keyup.space="finish"
            >
              {{ skipTranslation }}
            </a>
          </span>
          <div>
            <a
              v-if="activeIndex > 1"
              class="btn mr-1"
              :class="[isNewLayout && 'no-border']"
              tabindex="0"
              role="button"
              :aria-label="backTranslation"
              @keyup.enter="previousStep"
              @keyup.space="previousStep"
              @click="previousStep"
            >
              {{ backTranslation }}
            </a>
            <a
              v-if="!(activeIndex === steps.length - 1)"
              class="btn btn-primary"
              tabindex="0"
              role="button"
              :aria-label="nextTranslation"
              @click="nextStep"
              @keyup.enter="nextStep"
              @keyup.space="nextStep"
            >
              {{ nextTranslation }}
            </a>
            <a
              v-if="activeIndex === steps.length - 1"
              class="btn btn-primary"
              tabindex="0"
              role="button"
              :aria-label="finishTranslation"
              @click="finish"
              @keyup.enter="nextStep"
              @keyup.space="nextStep"
            >
              {{ finishTranslation }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
  getCurrentInstance,
} from 'vue'
import { has } from 'lodash'

import { usePopover } from '@/hooks'
import { invertPosition, Positions } from '@/models/enums/Positions'

import LpModal from '../modal'
import LpButton from '../button'

import LpGuidedTourStepImage from './LpGuidedTourStepImage.vue'

import type { Step } from './LpGuidedTour'
import { t } from '@/plugins/i18n'

export enum GuidedTourIntroductionStepStyles {
  CLASSIC = 'classic',
  NEW_FEATURE = 'new-feature',
  NEW_USER = 'new-user',
}

export default defineComponent({
  name: 'lp-guided-tour',
  components: {
    LpGuidedTourStepImage,
    LpModal,
    LpButton,
  },
  props: {
    steps: {
      type: Array as PropType<Step[]>,
      default: (): Step[] => [],
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
      required: true,
    },
    startingStep: {
      type: Number,
      default: 0,
    },
    autoposition: {
      type: Boolean,
      default: true,
    },
    dataUaUniqueId: {
      type: String,
      default: 'unique-guided-tour',
    },
    withScrim: {
      type: Boolean,
      default: true,
    },
    startTranslation: {
      type: String,
      default: t('LpGuidedTour.Next'),
    },
    nextTranslation: {
      type: String,
      default: t('LpGuidedTour.Next'),
    },
    backTranslation: {
      type: String,
      default: t('LpGuidedTour.Back'),
    },
    skipTranslation: {
      type: String,
      default: t('LpGuidedTour.Skip'),
    },
    finishTranslation: {
      type: String,
      default: t('LpGuidedTour.Finish'),
    },
    closeTranslation: {
      type: String,
      default: t('LpGuidedTour.Close'),
    },
    introductionStyle: {
      type: String,
      default: GuidedTourIntroductionStepStyles.CLASSIC,
      validation: (style: GuidedTourIntroductionStepStyles) =>
        [
          GuidedTourIntroductionStepStyles.CLASSIC,
          GuidedTourIntroductionStepStyles.NEW_FEATURE,
          GuidedTourIntroductionStepStyles.NEW_USER,
        ].includes(style),
    },
    highlightTargetElement: {
      type: Boolean,
    },
    closeWarningTitle: {
      type: String,
      default: undefined,
    },
    closeWarningContent: {
      type: String,
      default: undefined,
    },
    closeWarningStayButton: {
      type: String,
      default: t('LpGuidedTour.Stay'),
    },
    closeWarningExitButton: {
      type: String,
      default: t('LpGuidedTour.Exit'),
    },
    disableTeleport: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['step:next', 'previous:next', 'finish', 'skip'],

  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const steps = ref(props.steps)
    const open = ref(props.modelValue)
    const activeIndex = ref(props.startingStep)

    const currentStepTarget = ref<HTMLElement | null>(null)

    const confirmModalState = ref(false)

    const isOpen = computed(() => {
      return open.value
    })
    const currentStep = computed(() => props.steps[activeIndex.value])
    // const currentStep = reactive(props.steps[activeIndex.value])
    const currentStepSelector = computed(() =>
      currentStep.value && currentStep.value.selector
        ? document.querySelector(currentStep.value.selector)
        : null,
    )
    const currentStepPlacement = computed(() => currentStep.value.position)
    const currentStepEl = ref<Element | null>(null)

    const { placement, position, trigger, setPopoverPosition } = usePopover({
      ...props,
      trigger: currentStepSelector,
      popover: currentStepEl,
      placement: currentStepPlacement,
      disableSetPosition: true,
    })

    const arrowPlacement = computed(() => {
      if (!placement.value || placement.value === Positions.C) return ''
      const placementValue = placement.value
      try {
        const [placement1, placement2] = placementValue.split('-')

        return `card-arrow arrow-${invertPosition(placement1 as Positions)}${
          placement2 ? `-${invertPosition(placement2 as Positions)}` : ''
        }`
      } catch (e) {
        console.error(e)
        return ''
      }
    })

    watch(
      () => trigger.value,
      () => {
        if (props.highlightTargetElement) {
          if (currentStepTarget.value) {
            currentStepTarget.value.classList.remove(
              'guided-tour-highlight-element',
            )
            currentStepTarget.value = null
          }

          if (trigger.value) {
            trigger.value.classList.add('guided-tour-highlight-element')
            currentStepTarget.value = trigger.value
          }
        }
      },
    )

    watch(
      () => currentStep.value,
      () => {
        setPopoverPosition()
      },
    )

    const currentStepPosition = computed(() => {
      const output = {
        top: 'inherit',
        left: 'inherit',
      }

      if (position.value) {
        output.left = `${position.value.x}px`
        output.top = `${position.value.y}px`
      }

      return output
    })

    const nextStep = () => {
      activeIndex.value++
      // currentStep = props.steps[1]
      emit('step:next')
      instance?.proxy?.$forceUpdate()
    }

    const previousStep = () => {
      activeIndex.value--
      emit('previous:next')
    }

    const finish = () => {
      activeIndex.value = 0
      open.value = false
      confirmModalState.value = false
      emit('finish')
    }

    const abort = () => {
      if (!props.closeWarningTitle && !props.closeWarningContent) {
        activeIndex.value = 0
        open.value = false
        confirmModalState.value = false
        emit('finish')
      } else {
        confirmModalState.value = true
      }
    }

    const skip = () => {
      activeIndex.value = 0
      open.value = false
      confirmModalState.value = false
      emit('skip')
    }

    function keyboardNavigation(e: KeyboardEvent) {
      if (open.value) {
        if (e.key === 'ArrowRight') {
          if (activeIndex.value === steps.value.length - 1) {
            finish()
          } else {
            nextStep()
          }
        }

        if (e.key === 'ArrowLeft' && activeIndex.value > 0) {
          previousStep()
        }

        if (e.key === 'Escape') {
          finish()
        }
      }
    }

    onMounted(() => {
      document.body.addEventListener('keyup', keyboardNavigation)
    })

    onUnmounted(() => {
      document.body.removeEventListener('keyup', keyboardNavigation)
    })

    watch(
      () => props.modelValue,
      (newValue) => {
        open.value = newValue
      },
    )

    const isNewLayout = computed(
      () =>
        props.introductionStyle !== GuidedTourIntroductionStepStyles.CLASSIC,
    )

    const hasImageAndIsNewLayout = computed(
      () =>
        isNewLayout.value && currentStep.value.img && activeIndex.value === 0,
    )

    return {
      activeIndex,
      currentStep,
      isOpen,
      nextStep,
      previousStep,
      finish,
      abort,
      skip,
      currentStepEl,
      placement,
      arrowPlacement,
      currentStepPosition,
      setPopoverPosition,
      GuidedTourIntroductionStepStyles,
      isNewLayout,
      hasImageAndIsNewLayout,
      confirmModalState,
    }
  },
  methods: { has },
})
</script>
